const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // Helper function to close search
    this.closeSearch = () => {
      document.body.classList.remove("show-search");
      this.querySelector(".main-search__input").value = "";
      this.classList.remove("main-search--has-results");
      this.classList.remove("main-search--loading");
      const searchSuggestions = this.querySelector(".search-suggestions");
      if (searchSuggestions) {
        searchSuggestions.innerHTML = "";
      }
      const content = document.getElementById("content");
      if (content) {
        content.classList.remove("overlay-active");
      }
      
      // Clear any pending styling timer and reset text
      if (this.stylingTimer) {
        clearTimeout(this.stylingTimer);
        this.stylingTimer = null;
      }
      
      // Clear placeholder typing animation
      if (this.placeholderTimer) {
        clearTimeout(this.placeholderTimer);
        this.placeholderTimer = null;
      }
      
      this.querySelectorAll(".main-search__loading-text").forEach(text => {
        text.textContent = "";
      });
    };

    // Clicking close button
    this.querySelectorAll(".main-search__close").forEach((el) => {
      el.addEventListener("click", (evt) => {
        evt.preventDefault();
        this.closeSearch();
      });
    });

    // Click outside to close
    document.addEventListener("click", (evt) => {
      const isClickInside = this.contains(evt.target);
      const isSearchActive = this.classList.contains("main-search--has-results") || 
                            this.classList.contains("main-search--loading");
      
      if (!isClickInside && isSearchActive) {
        this.closeSearch();
      }
    });

    // Pressing escape key
    this.querySelector(".main-search__input").addEventListener(
      "keyup",
      (evt) => {
        if (evt.key === "Escape") {
          this.querySelector(".main-search__close").dispatchEvent(
            new Event("click")
          );
        }
      }
    );

    // Initialize placeholder typing animation
    this.initPlaceholderTyping();

    // Search as you type
    if (this.dataset.quickSearch === "true") {
      this.initQuickSearch();
    }
  }

  initPlaceholderTyping() {
    const searchInput = this.querySelector(".main-search__input");
    if (!searchInput) return;

    // Helper function to generate random future date
    const getRandomDate = () => {
      const currentDate = new Date();
      // Get a date between 7 and 120 days from now
      const daysAhead = Math.floor(Math.random() * 113) + 7;
      const futureDate = new Date(currentDate);
      futureDate.setDate(currentDate.getDate() + daysAhead);
      
      const day = futureDate.getDate();
      const monthName = futureDate.toLocaleDateString('en-GB', { month: 'long' });
      
      return `${day} ${monthName}`;
    };

    // Get suggestions from data attribute or use defaults
    const suggestionsData = this.dataset.searchSuggestions || 
      "Sparkly dresses for a party night\nMinimalist white dress for my wedding\nElegant gown for a black tie event\nA black maxi dress for a winter ball\nStatement dress for my birthday party";
    
    // Split by newlines and format with dates
    const baseSuggestions = suggestionsData.split('\n').filter(s => s.trim());
    const prompts = baseSuggestions.map(suggestion => {
      return `${suggestion.trim()} on ${getRandomDate()}`;
    });

    let currentPromptIndex = 0;
    let currentCharIndex = 0;
    let isTyping = true;
    let typingTimer = null;

    // Shuffle prompts on init
    const shuffledPrompts = [...prompts].sort(() => Math.random() - 0.5);

    const typeNextChar = () => {
      const currentPrompt = shuffledPrompts[currentPromptIndex];
      
      if (isTyping) {
        // Typing forward with speech marks around the suggestion
        if (currentCharIndex <= currentPrompt.length) {
          const text = 'Try "' + currentPrompt.substring(0, currentCharIndex) + '"';
          searchInput.placeholder = text;
          currentCharIndex++;
          typingTimer = setTimeout(typeNextChar, 50); // Faster typing speed
        } else {
          // Pause at end before clearing
          isTyping = false;
          typingTimer = setTimeout(typeNextChar, 1500); // Shorter pause at end
        }
      } else {
        // Clear instantly and move to next prompt
        currentPromptIndex = (currentPromptIndex + 1) % shuffledPrompts.length;
        currentCharIndex = 0;
        isTyping = true;
        searchInput.placeholder = 'Try "';
        typingTimer = setTimeout(typeNextChar, 300); // Brief pause before next prompt
      }
    };

    // Only animate when search input is focused and empty
    searchInput.addEventListener("focus", () => {
      if (!searchInput.value) {
        currentCharIndex = 0;
        isTyping = true;
        typeNextChar();
      }
    });

    // Stop animation when user starts typing or loses focus
    searchInput.addEventListener("input", () => {
      if (searchInput.value) {
        clearTimeout(typingTimer);
        searchInput.placeholder = "Search anything";
      }
    });

    searchInput.addEventListener("blur", () => {
      clearTimeout(typingTimer);
      searchInput.placeholder = "Search anything";
    });

    // Store timer reference for cleanup
    this.placeholderTimer = typingTimer;
  }

  initQuickSearch() {
    const main = document.getElementById("content");
    const searchInput = this.querySelector(".main-search__input");
    const searchTimeoutThrottle = 300; // Debounce: wait 300ms after last keystroke
    const resultLimit = 4;
    const includeMeta = this.dataset.quickSearchMeta === "true";
    const featuredBadgeLabel = this.dataset.featuredBadgeLabel || "Top result";
    let searchTimeoutID = -1;
    let searchAbortController = null;

    const applyArrayParam = (url, key, values) => {
      url.searchParams.delete(key);
      if (!values || !values.length) {
        return;
      }
      values.forEach((value) => {
        if (value) {
          url.searchParams.append(key, value);
        }
      });
    };
    const ensureFeaturedBadge = (block) => {
      if (!block) return;
      let badge = block.querySelector(".product-card-v2__badge");
      if (!badge) {
        badge = document.createElement("span");
        badge.className = "product-card-v2__badge";
        block.insertAdjacentElement("afterbegin", badge);
      }
      badge.classList.add("product-card-v2__badge--featured");
      badge.textContent = featuredBadgeLabel;
    };

    const handleInputChange = () => {
      const resultsBox = this.querySelector(".main-search__results");
      const valueToSearch = searchInput.value;

      // Only search if search string longer than 2, and it has changed
      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        // Save previous value
        this.oldSearchValue = valueToSearch;

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort("Existing request not needed");
          searchAbortController = null;
        }

        // Kill previous search (debouncing)
        clearTimeout(searchTimeoutID);

        // Create URL for full search results
        const form = searchInput.closest("form");
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => {
          linkURL.searchParams.set(key, value);
        });
        main.classList.add("overlay-active");

        // Check if we had results before removing the class
        const hadResults = this.classList.contains("main-search--has-results");
        
        // Show loading - add loading class to trigger glowing border and loading text
        this.classList.remove(
          "main-search--has-results",
          "main-search--results-on-multiple-lines",
          "main-search--no-results"
        );
        this.classList.add("main-search--loading");
        
        // Handle loading text state
        const loadingTexts = this.querySelectorAll(".main-search__loading-text");
        const currentText = loadingTexts[0]?.textContent || "";
        
        // Reset to "Thinking..." if: 
        // 1. No text is showing (first search)
        // 2. Previous search had completed (new search after results)
        if (!currentText || currentText === "" || hadResults) {
          loadingTexts.forEach(text => {
            text.textContent = "Thinking...";
          });
          
          // Clear any existing styling timer
          if (this.stylingTimer) {
            clearTimeout(this.stylingTimer);
          }
          
          // Set new timer to change to "Styling..."
          this.stylingTimer = setTimeout(() => {
            if (this.classList.contains("main-search--loading")) {
              loadingTexts.forEach(text => {
                text.textContent = "Styling...";
              });
            }
          }, 1000);
        } else if (currentText === "Styling...") {
          // Already on "Styling...", keep it and don't set a new timer
          if (this.stylingTimer) {
            clearTimeout(this.stylingTimer);
          }
        }
        
        // Clear previous results but don't show the results spinner in the modal
        resultsBox.innerHTML = "";

        // Do next search (in X milliseconds)
        searchTimeoutID = setTimeout(async () => {
          searchAbortController = new AbortController();
          
          // Enhance search term with AI (keeps original in search bar)
          let enhancedSearchTerm = valueToSearch;
          let parsedSearchData = null;
          if (window.AISearchHelper) {
            try {
              parsedSearchData = await window.AISearchHelper.enhance(valueToSearch);
              if (parsedSearchData?.keywords) {
                enhancedSearchTerm = parsedSearchData.keywords;
              }
            } catch (error) {
              parsedSearchData = null;
              enhancedSearchTerm = valueToSearch;
            }
          }
          linkURL.searchParams.set("q", enhancedSearchTerm);

          if (parsedSearchData?.original) {
            linkURL.searchParams.set("original_query", parsedSearchData.original);
          } else {
            linkURL.searchParams.delete("original_query");
          }

          if (parsedSearchData?.sizes?.length) {
            applyArrayParam(linkURL, "filter.v.option.size", parsedSearchData.sizes);
          } else {
            linkURL.searchParams.delete("filter.v.option.size");
          }

          if (parsedSearchData?.colors?.length) {
            applyArrayParam(linkURL, "filter.p.m.custom.color", parsedSearchData.colors);
          } else {
            linkURL.searchParams.delete("filter.p.m.custom.color");
          }
          
          let ajaxUrl;

          if (theme.Shopify.features.predictiveSearch) {
            // use the API with enhanced keywords (build relative path to avoid CORS)
            const searchParams = new URLSearchParams({
              q: enhancedSearchTerm,
              "section_id": "predictive-search",
              "resources[type]": "product",
              "resources[limit]": resultLimit,
              "resources[limit_scope]": "each",
              "resources[options][fields]": includeMeta
                ? "title,product_type,variants.title,vendor,tag,variants.sku,variants.barcode,body"
                : "title,product_type,variants.title,vendor,tag,body"
            });
            // Build relative URL path (not absolute)
            ajaxUrl = `${theme.routes.predictiveSearch}?${searchParams.toString()}`;
          } else {
            // use the theme template fallback
            ajaxUrl = new URL(linkURL);
            ajaxUrl.searchParams.set("section_id", "main-search");
            ajaxUrl = ajaxUrl.toString();
          }

          fetch(ajaxUrl, {
            method: "get",
            signal: searchAbortController.signal,
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }
              return response.text();
            })
            .then((response) => {
              const template = document.createElement("template");
              template.innerHTML = response;
              let resultsList = null;
              if (theme.Shopify.features.predictiveSearch) {
                resultsList = template.content.querySelector(".product-grid");
              } else {
                resultsList = template.content.querySelector(
                  ".section-search-template .product-grid"
                );
              }

              // reset previous contents
              searchAbortController = null;
              this.classList.remove(
                "main-search--has-results",
                "main-search--results-on-multiple-lines",
                "main-search--no-results"
              );
              resultsBox.innerHTML = "";

              const resultsProducts = document.createElement("div");
              resultsProducts.className =
                "main-search__results__products collection-listing";
              resultsProducts.innerHTML = "<div></div>";
              const resultsPages = document.createElement("div");
              resultsPages.className = "main-search__results__pages";

              resultsProducts.firstElementChild.className =
                resultsList.className;

              // Convert standard blocks into quick search result format
              const featuredHandles = [];
              const allProductBlocks = resultsList
                .querySelectorAll(
                  ".product-block:not(.collection-block):not(.page-block)"
                );
              
              allProductBlocks.forEach((block, index) => {
                  if (index < resultLimit) {
                    const productHandle = block.dataset.productHandle;
                    if (productHandle && !featuredHandles.includes(productHandle)) {
                      featuredHandles.push(productHandle);
                    }
                    block.classList.add("main-search-result");
                    ensureFeaturedBadge(block);
                    block
                      .querySelectorAll(".btn.quickbuy-toggle")
                      .forEach((el) => el.remove());
                    block
                      .querySelectorAll(".quickbuy-toggle")
                      .forEach((el) => el.classList.remove("quickbuy-toggle"));
                    resultsProducts.firstElementChild.appendChild(block);
                  }
                });

              resultsList
                .querySelectorAll(".product-block.page-block")
                .forEach((block, index) => {
                  const item = document.createElement("a");
                  item.className =
                    "main-search-result main-search-result--page";
                  item.href = block.querySelector("a").href;
                  item.innerHTML =
                    '<div class="main-search-result__text"></div>';
                  item.firstElementChild.innerText =
                    block.querySelector(".page-block__title").innerText;
                  
                  // Store index for later dynamic delay calculation
                  item.dataset.pageIndex = index;
                  
                  resultsPages.appendChild(item);
                });

              this.classList.remove("main-search--loading");

              const areProducts = !!resultsProducts.querySelector(
                ".main-search-result"
              );
              const arePages = !!resultsPages.querySelector(
                ".main-search-result"
              );
              if (areProducts || arePages) {
                // Numerous results
                this.classList.add("main-search--has-results");
                this.classList.toggle(
                  "main-search--results-on-multiple-lines",
                  resultsProducts.querySelectorAll(".product-block").length > 4
                );
                if (areProducts) {
                  resultsBox.appendChild(resultsProducts);
                }
                if (arePages) {
                  const heading = document.createElement("h6");
                  heading.className = "main-search-result__heading";
                  heading.innerHTML = theme.strings.generalSearchPages;
                  
                  // Set dynamic animation delay based on number of product cards
                  const productCount = resultsProducts.querySelectorAll(".product-block").length;
                  const headingDelay = (productCount * 0.3) + 0.3;
                  heading.style.animationDelay = `${headingDelay}s`;
                  resultsPages.style.animationDelay = `${headingDelay}s`;
                  
                  // Set dynamic delays for individual page results
                  const pageItems = resultsPages.querySelectorAll(".main-search-result--page");
                  pageItems.forEach((item, index) => {
                    const pageDelay = headingDelay + ((index + 1) * 0.3);
                    item.style.animationDelay = `${pageDelay}s`;
                  });
                  
                  resultsPages.insertAdjacentElement("afterbegin", heading);
                  resultsBox.appendChild(resultsPages);
                }

                const allLink = document.createElement("a");
                allLink.className =
                  "main-search__results-all-link btn btn--secondary";
                if (featuredHandles.length) {
                  linkURL.searchParams.set(
                    "featured_products",
                    featuredHandles.join(",")
                  );
                }
                allLink.href = linkURL.toString();
                allLink.innerHTML = theme.strings.generalSearchViewAll;
                
                // Set dynamic animation delay based on number of product cards
                const productCount = resultsProducts.querySelectorAll(".product-block").length;
                const buttonDelay = (productCount * 0.3) + 0.3; // Each card is 0.3s apart
                allLink.style.animationDelay = `${buttonDelay}s`;
                
                resultsBox.appendChild(allLink);
              } else {
                // No results - show empty state with user's search term
                this.classList.add("main-search--no-results");
                this.classList.add("main-search--has-results"); // Ensure modal displays
                
                const emptyMessage = document.createElement("div");
                emptyMessage.className = "main-search__empty-message";
                emptyMessage.innerHTML = `
                  <div style="text-align: center; padding: 60px 20px;">
                    <h3 style="font-size: 24px; margin-bottom: 16px; font-weight: 500;">No results found</h3>
                    <p style="font-size: 16px; color: #666; margin-bottom: 8px;">
                      We couldn't find anything matching <strong>"${valueToSearch}"</strong>
                    </p>
                    <p style="font-size: 14px; color: #999;">
                      Try different keywords or browse our collections
                    </p>
                  </div>
                `;
                resultsBox.appendChild(emptyMessage);
                
                // Add a "Browse All" button
                const browseLink = document.createElement("a");
                browseLink.className = "main-search__results-all-link btn btn--secondary";
                browseLink.href = "/collections/all";
                browseLink.innerHTML = "Browse All Products";
                browseLink.style.marginTop = "20px";
                resultsBox.appendChild(browseLink);
              }
            })
            .catch((error) => {
              // Silent error handling
            });
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        // Abandon current search and close if empty
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort("Existing request not needed");
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);
        
        // Clear styling timer and reset text
        if (this.stylingTimer) {
          clearTimeout(this.stylingTimer);
          this.stylingTimer = null;
        }

        // Clear results and reset loading text
        this.classList.remove(
          "main-search--has-results",
          "main-search--results-on-multiple-lines",
          "main-search--loading"
        );
        resultsBox.innerHTML = "";
        this.querySelectorAll(".main-search__loading-text").forEach(text => {
          text.textContent = "";
        });
        
        // Remove overlay when search is empty (backspaced to nothing)
        const content = document.getElementById("content");
        if (content) {
          content.classList.remove("overlay-active");
        }
      }
    };

    searchInput.addEventListener("keyup", handleInputChange.bind(this));
    searchInput.addEventListener("change", handleInputChange.bind(this));
  }
};

window.customElements.define("main-search", MainSearch);
